import React, { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import StepSelector from "./step-selector/StepSelector";
import Step1CustomerInfo from "./step-1-customer-info/Step1CustomerInfo";
import Step2VehicleInfo from "./step-2-vehicle-info/Step2VehicleInfo";
import Step3PickupInfo from "./step-3-pickup-info/Step3PickupInfo";
import Step4DeliveryInfo from "./step-4-delivery-info/Step4DeliveryInfo";
import Step5DriverInfo from "./step-5-driver-info/Step5DriverInfo";
import TestDataSelector from "./TestDataSelector";
import {
  transformLoadDataForAPI,
  transformLoadDataForForm,
  validateLoadData,
  removeEmptyFields,
  removeEmptyFieldsForPatch,
  hasFileObjects,
  extractAllFileObjects,
  extractImageKeys,
} from "./loadDataTransformer";
import loadService from "../../../services/loadService";
import carrierService from "../../../services/carrierService";
import ResponseModal from "../../modals/response-modal";
import Loading from "../../loading-animation";
import "./LoadAddForm.scss";
import { useUser } from "../../../contexts/UserContext";
import {
  canCreateLoads,
  canEditLoads,
  getLoadStepAccess,
  getUserRole
} from "../../../utils/roleAccess";

const LoadAddForm = ({
  loadId,
  loadData: initialLoadData,
  isEditMode = false,
  onClose,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const role = getUserRole(user);
  const stepAccess = getLoadStepAccess(role);
  const canCreate = canCreateLoads(role);
  const canEdit = canEditLoads(role);
  const canSubmit = isEditMode ? canEdit : canCreate;
  const [currentStep, setCurrentStep] = useState(1);
  const [isStep5Visited, setIsStep5Visited] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [loading, setLoading] = useState(isEditMode && !initialLoadData);

  // Response Modal State
  const [responseModal, setResponseModal] = useState({
    isOpen: false,
    status: "success", // 'success' or 'error'
    title: "",
    message: "",
  });

  const defaultFormValues = {
    orderId: "",
    loadId: "",
    customer: {
      companyName: "",
      email: "",
      customerAddress: {
        address: "",
        city: "",
        state: "",
        zipCode: "",
      },
    },
    customerEmails: [],
    customerRate: "",
    carrierRate: "",
    paymentMethod: "",
    paymentMethodOther: "",
    paymentTerms: "",
    type: {
      freight: false,
      vehicle: false,
    },
    vehicle: {
      shipment: [],
      specialRequirements: "",
      vehicleImages: [],
    },
    freight: {
      shipment: [],
      freightImages: [],
    },
    pickup: {
      locationName: "",
      address: {
        address: "",
        city: "",
        state: "",
        zipCode: "",
      },
      contactPhone: "",
      notes: "",
      date: "",
      images: [],
    },
    delivery: {
      locationName: "",
      address: {
        address: "",
        city: "",
        state: "",
        zipCode: "",
      },
      contactPhone: "",
      notes: "",
      date: "",
      images: [],
    },
    carrier: {
      id: "",
      name: "",
      phoneNumber: "",
      email: "",
      companyName: "",
      dba: "",
      mcNumber: "",
      dotNumber: "",
      address: {
        address: "",
        city: "",
        state: "",
        zipCode: "",
      },
      equipment: [],
      equipmentType: [],
      size: [],
      capabilities: [],
      certifications: [],
    },
    carrierEmails: [],
    carrierPhotos: [],
    deletedCarrierImages: [],
    deletedPickupImages: [],
    deletedDeliveryImages: [],
    deletedVehicleImages: [],
    deletedFreightImages: [],
    deletedBolDocuments: [],
    deletedRateConfirmationDocuments: [],
    deletedDocuments: [],
    insurance: {
      type: "",
      customAmount: "",
    },
    status: "Listed",
    dates: {
      assignedDate: "",
      deadline: "",
      pickupDate: "",
      pickupDateType: "Exact",
      pickupDateStart: "",
      pickupDateEnd: "",
      deliveryDate: "",
      deliveryDateType: "Exact",
      deliveryDateStart: "",
      deliveryDateEnd: "",
      aging: "",
    },
    fees: [],
    tonu: {
      enabled: false,
      carrierRate: "",
      customerRate: "",
    },
    tracking: "",
    bolDocuments: [],
    rateConfirmationDocuments: [],
    documents: [],
    tempEntityId: "",
    images: [],
    pdfs: [],
  };

  const formDefaultValues = React.useMemo(() => {
    if (isEditMode && initialLoadData) {
      return transformLoadDataForForm(initialLoadData);
    }
    return defaultFormValues;
  }, [isEditMode, initialLoadData]);

  const methods = useForm({
    defaultValues: formDefaultValues,
  });

  // Load and set form data when editing (if loadId provided but no initialLoadData)
  useEffect(() => {
    if (isEditMode && loadId && !initialLoadData) {
      const fetchLoad = async () => {
        try {
          setLoading(true);
          setSubmitError("");
          const response = await loadService.getLoadById(loadId);
          const load = response?.data?.data || response?.data || response;
          
          if (!load) {
            throw new Error("Load not found");
          }
          
          const formData = transformLoadDataForForm(load);
          
          if (!formData) {
            throw new Error("Failed to transform load data");
          }
          
          methods.reset(formData);
          // TEMPORARILY DISABLED: Original image keys tracking removed
          // saveOriginalImageKeys(formData);
        } catch (error) {
          const errorMessage = error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "Failed to load load data";
          setSubmitError(errorMessage);
          console.error('[LoadForm] Error loading load data:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchLoad();
      } else if (isEditMode && initialLoadData) {
      // If initialLoadData is provided, reset form with it
      try {
        const formData = transformLoadDataForForm(initialLoadData);
        if (formData) {
          methods.reset(formData);
          // TEMPORARILY DISABLED: Original image keys tracking removed
          // saveOriginalImageKeys(formData);
        }
      } catch (error) {
        console.error('[LoadForm] Error transforming initial load data:', error);
        setSubmitError("Failed to process load data");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, loadId, initialLoadData]);

  const { handleSubmit, trigger, getValues, formState } = methods;

  useEffect(() => {
    if (currentStep === 5) {
      setIsStep5Visited(true);
    }
  }, [currentStep]);
  
  // TEMPORARILY DISABLED: Original image keys tracking removed
  // const [originalImageKeys, setOriginalImageKeys] = React.useState({});
  // const saveOriginalImageKeys = React.useCallback((formData) => { ... }, []);

  // Helper function to check if a value is empty (should be filtered out)
  const isEmptyValue = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'object' && Object.keys(value).length === 0) return true;
    return false;
  };

  // TEMPORARILY DISABLED: Helper function to extract only dirty (changed) fields from form data
  // Uses react-hook-form's dirtyFields to track which fields have been modified
  // IMPORTANT: Only includes fields that were actually changed, excludes empty values unless explicitly cleared
  const getDirtyFieldsData = (data, dirtyFields) => {
    // Handle null/undefined cases
    if (!data || typeof data !== "object") {
      return {};
    }

    if (
      !dirtyFields ||
      typeof dirtyFields !== "object" ||
      Object.keys(dirtyFields).length === 0
    ) {
      return {};
    }

    const dirtyData = {};

    // Fields that can be explicitly cleared (empty string/null is meaningful)
    const clearableFields = [
      'customerRate', 
      'carrierRate', 
      'orderId', 
      'tracking', 
      'notes',
      'status',
      'paymentMethod',
      'paymentTerms',
      'customerEmails',
      'carrierEmails'
    ];

    for (const key in dirtyFields) {
      if (!dirtyFields.hasOwnProperty(key)) continue;
      if (!(key in data)) continue;

      const dirtyValue = dirtyFields[key];
      const dataValue = data[key];

      // Skip if dataValue is undefined
      if (dataValue === undefined) continue;

      if (dirtyValue === true) {
        // Field is marked as dirty - include the value
        // For clearable fields, include even if empty (user explicitly cleared it)
        // For image/document arrays, include even if empty (user explicitly cleared all images)
        const isImageArray = ['carrierPhotos', 'bolDocuments', 'rateConfirmationDocuments', 'documents'].includes(key) && Array.isArray(dataValue);
        if (clearableFields.includes(key) || isImageArray || !isEmptyValue(dataValue)) {
          dirtyData[key] = dataValue;
        }
      } else if (typeof dirtyValue === "object" && dirtyValue !== null) {
        if (Array.isArray(dirtyValue)) {
          // Array field - if any item is dirty, include the whole array
          // Check if any item in the array is marked as dirty
          const hasDirtyItem = dirtyValue.some(
            (item) =>
              item === true || (typeof item === "object" && item !== null)
          );
          if (hasDirtyItem && Array.isArray(dataValue)) {
            // For clearable arrays (emails), include even if empty
            // For other arrays, skip if empty
            if (clearableFields.includes(key) || dataValue.length > 0) {
              dirtyData[key] = dataValue;
            }
          }
        } else {
          // Nested object - recursively get dirty fields
          if (
            dataValue &&
            typeof dataValue === "object" &&
            !Array.isArray(dataValue)
          ) {
            let nestedDirty = getDirtyFieldsData(dataValue, dirtyValue);
            
            // Специальная обработка для vehicle, freight, pickup и delivery: если image arrays dirty и пустые - включить
            // Это важно для случая когда пользователь удалил все изображения
            let hasEmptyImageArray = false;
            if (key === 'vehicle' || key === 'freight' || key === 'pickup' || key === 'delivery') {
              const imageFieldName = key === 'vehicle' ? 'vehicleImages' : 
                                     key === 'freight' ? 'freightImages' : 
                                     'images'; // для pickup и delivery поле называется 'images'
              const imageDirtyValue = dirtyValue[imageFieldName];
              const imageDataValue = dataValue[imageFieldName];
              
              // Проверяем разные варианты как react-hook-form может пометить массив как dirty:
              // 1. imageDirtyValue === true (весь массив dirty)
              // 2. imageDirtyValue === [] (массив dirty, но пустой)
              // 3. imageDirtyValue !== undefined (массив помечен как dirty)
              
              const isImageArrayDirty = imageDirtyValue === true || 
                                        (Array.isArray(imageDirtyValue) && imageDirtyValue.length === 0) ||
                                        (imageDirtyValue !== undefined && typeof imageDirtyValue === 'object');
              
              // Если image array dirty и массив пустой - это явное удаление всех изображений
              if (isImageArrayDirty && Array.isArray(imageDataValue) && imageDataValue.length === 0) {
                if (!nestedDirty) {
                  nestedDirty = {};
                }
                nestedDirty[imageFieldName] = [];
                hasEmptyImageArray = true;
              }
            }
            
            // Include nested object if it has meaningful fields OR if it has empty image array (deletion)
            if (nestedDirty && (Object.keys(nestedDirty).length > 0 || hasEmptyImageArray)) {
              dirtyData[key] = nestedDirty;
            }
          } else if (dataValue !== undefined && dataValue !== null) {
            // If marked as dirty but not an object, include the whole value
            if (!isEmptyValue(dataValue)) {
              dirtyData[key] = dataValue;
            }
          }
        }
      }
    }

    return dirtyData;
  };

  const steps = [
    { component: Step1CustomerInfo, title: "Customer Information" },
    { component: Step2VehicleInfo, title: "Shipment" },
    { component: Step3PickupInfo, title: "Pickup Location" },
    { component: Step4DeliveryInfo, title: "Delivery Location" },
    { component: Step5DriverInfo, title: "Carrier Information" },
  ];

  const isStepEditable = (stepNumber) => {
    if (stepAccess.readOnly) return false;
    if (stepNumber <= 4) return stepAccess.step1to4;
    if (stepNumber === 5) return stepAccess.step5;
    return false;
  };

  const isCurrentStepEditable = isStepEditable(currentStep);
  const currentStepReadOnly = !isCurrentStepEditable;
  const canSubmitCurrentStep = canSubmit && isCurrentStepEditable;

  const getFirstErrorMessage = (errObj) => {
    if (!errObj || typeof errObj !== "object") return null;
    const stack = [errObj];
    const visited = new Set();
    while (stack.length > 0) {
      const node = stack.pop();
      if (!node || typeof node !== "object") continue;
      if (visited.has(node)) continue;
      visited.add(node);
      if (typeof node.message === "string" && node.message.trim() !== "") return node.message;
      for (const value of Object.values(node)) {
        if (value && typeof value === "object") stack.push(value);
      }
    }
    return null;
  };

  const validateTransition = async (fromStep, toStep) => {
    for (let step = fromStep; step < toStep; step++) {
      const fieldsToValidate = getCurrentStepFields(step - 1);
      const isValid = await trigger(fieldsToValidate);
      if (!isValid) {
        const firstMessage = getFirstErrorMessage(formState?.errors);
        setResponseModal({
          isOpen: true,
          status: "error",
          title: "Validation error",
          message: firstMessage || "Please complete required fields to continue.",
        });
        return false;
      }
    }
    return true;
  };

  const handleStepClick = async (stepNumber) => {
    if (stepNumber < currentStep) {
      setCurrentStep(stepNumber);
      return;
    }

    if (!isStepEditable(currentStep)) {
      if (stepNumber <= steps.length) {
        setCurrentStep(stepNumber);
      }
      return;
    }

    const targetStep = Math.min(stepNumber, steps.length);
    if (targetStep <= currentStep) return;

    const ok = await validateTransition(currentStep, targetStep);
    if (ok) setCurrentStep(targetStep);
  };

  const getCurrentStepFields = (stepIndex) => {
    const fieldMappings = [
      ["customer.companyName"], // Step 1
      ["type.freight", "type.vehicle"], // Step 2
      ["pickup.address.city", "pickup.address.state"], // Step 3
      ["delivery.address.city", "delivery.address.state"], // Step 4
      [],
    ];

    return fieldMappings[stepIndex] || [];
  };

  const handleNext = async (e) => {
    // Prevent form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!isStepEditable(currentStep)) {
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      }
      return;
    }

    const targetStep = currentStep + 1;
    if (targetStep > steps.length) return;

    const ok = await validateTransition(currentStep, targetStep);
    if (ok) {
      setCurrentStep(targetStep);
      return;
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle response modal close
  const handleResponseModalClose = () => {
    setResponseModal((prev) => ({ ...prev, isOpen: false }));
    // Stay on the form page after closing modal
  };

  const handleFormInvalid = (errors) => {
    const firstMessage = getFirstErrorMessage(errors);
    setResponseModal({
      isOpen: true,
      status: "error",
      title: "Validation error",
      message: firstMessage || "Please complete required fields.",
    });
  };

  // Helper function to process carrier creation/update
  const processCarrier = async (loadData) => {
    const hasCarrierData =
      loadData.carrier &&
      (loadData.carrier.name?.trim() ||
        loadData.carrier.phoneNumber?.trim() ||
        loadData.carrier.email?.trim() ||
        loadData.carrier.companyName?.trim());

    if (!hasCarrierData) {
      delete loadData.carrier;
      return;
    }

    const isMongoId = (id) => /^[0-9a-fA-F]{24}$/.test(String(id || ""));
    const carrierId = loadData.carrier?.id;
    const isNewCarrier = !carrierId || !isMongoId(carrierId);

    if (!isNewCarrier) return;

    try {
      const equipmentPayload = Array.isArray(loadData.carrier?.equipment)
        ? loadData.carrier.equipment
            .map(item => ({
              type: String(item?.type || '').trim(),
              sizes: Array.isArray(item?.sizes)
                ? item.sizes.map(size => String(size || '').trim()).filter(Boolean)
                : []
            }))
            .filter(item => item.type)
        : [];
      const equipmentType = equipmentPayload.map(item => item.type);
      const size = Array.from(new Set(equipmentPayload.flatMap(item => item.sizes || [])));

      const carrierData = {
        companyName: loadData.carrier.companyName || "",
        dba: loadData.carrier.dba || "",
        mcNumber: loadData.carrier.mcNumber || "",
        dotNumber: loadData.carrier.dotNumber || "",
        name: loadData.carrier.name || "",
        phoneNumber: loadData.carrier.phoneNumber || "",
        email: (loadData.carrier.email && loadData.carrier.email !== 'undefined' && loadData.carrier.email !== 'null') 
          ? loadData.carrier.email.trim() 
          : "",
        address: loadData.carrier.address || {},
        people: Array.isArray(loadData.carrier.people) && loadData.carrier.people.length > 0
          ? loadData.carrier.people.filter(person => person && person.fullName && person.fullName.trim() !== '')
          : [],
        equipment: equipmentPayload,
        equipmentType: equipmentType.length > 0
          ? equipmentType
          : Array.isArray(loadData.carrier.equipmentType) && loadData.carrier.equipmentType.length > 0
          ? loadData.carrier.equipmentType.filter(type => type && type.trim() !== '')
          : loadData.carrier.equipmentType
            ? Array.isArray(loadData.carrier.equipmentType)
              ? loadData.carrier.equipmentType.filter(type => type && type.trim() !== '')
              : [loadData.carrier.equipmentType].filter(type => type && type.trim() !== '')
            : [],
        size: size.length > 0
          ? size
          : Array.isArray(loadData.carrier.size) && loadData.carrier.size.length > 0
          ? loadData.carrier.size.filter(s => s && s.trim() !== '')
          : loadData.carrier.size
            ? Array.isArray(loadData.carrier.size)
              ? loadData.carrier.size.filter(s => s && s.trim() !== '')
              : [loadData.carrier.size].filter(s => s && s.trim() !== '')
            : [],
        capabilities: loadData.carrier.capabilities || [],
        certifications: loadData.carrier.certifications || [],
      };

      const dotNumber = carrierData.dotNumber?.trim();
      const mcNumber = carrierData.mcNumber?.trim();
      const email = carrierData.email?.trim();

      if (dotNumber || mcNumber || email) {
        const existingCarrier = await carrierService.findCarrierByUniqueFields(
          dotNumber,
          mcNumber,
          email
        );    

        if (existingCarrier) {
          loadData.carrier.id = existingCarrier._id || existingCarrier.id;
          return;
        }
      }

      const cleanedCarrierData = removeEmptyFields(carrierData);
      if (cleanedCarrierData && Object.keys(cleanedCarrierData).length > 0) {
        const carrierResponse = await carrierService.createCarrier(cleanedCarrierData);
        const createdCarrier = carrierResponse?.data?.data || carrierResponse?.data;
        const newCarrierId = createdCarrier?._id || createdCarrier?.id;

        if (newCarrierId) {
          loadData.carrier.id = newCarrierId;
        }
      }
    } catch (carrierError) {
      const errorMessage =
        carrierError?.response?.data?.message ||
        carrierError?.message ||
        "";

      if (
        errorMessage.toLowerCase().includes("duplicate") ||
        errorMessage.toLowerCase().includes("already exists") ||
        carrierError?.response?.status === 409
      ) {
        const dotNumber = loadData.carrier?.dotNumber?.trim();
        const mcNumber = loadData.carrier?.mcNumber?.trim();
        const email = loadData.carrier?.email?.trim();

        if (dotNumber || mcNumber || email) {
          const existingCarrier = await carrierService.findCarrierByUniqueFields(
            dotNumber,
            mcNumber,
            email
          );
          if (existingCarrier) {
            loadData.carrier.id = existingCarrier._id || existingCarrier.id;
            return;
          }
        }
      }

      throw new Error(`Failed to create carrier: ${errorMessage || "Unknown error"}`);
    }
  };

  // Helper function to clean blob URLs and File objects from data
  const cleanFileData = (value) => {
    if (Array.isArray(value)) {
      const cleaned = value
        .filter(item => {
          if (item && typeof item === 'object' && item.file instanceof File) return false;
          if (typeof item === 'string' && item.startsWith('blob:')) return false;
          if (item && typeof item === 'object' && item.url && item.url.startsWith('blob:')) return false;
          return true;
        })
        .map(item => {
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            return cleanFileData(item);
          }
          return item;
        });
      return cleaned;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const cleaned = {};
      let hasValues = false;
      for (const key in value) {
        const cleanedValue = cleanFileData(value[key]);
        if (cleanedValue !== undefined) {
          cleaned[key] = cleanedValue;
          hasValues = true;
        }
      }
      return hasValues ? cleaned : undefined;
    }
    if (typeof value === 'string' && value.startsWith('blob:')) {
      return undefined;
    }
    return value;
  };

  // Helper function to clean blob URLs from arrays (keep only S3 keys)
  const cleanBlobUrls = (arr) => {
    if (!Array.isArray(arr)) return arr;
    return arr.filter(item => {
      if (typeof item === 'string') return !item.startsWith('blob:');
      if (item && typeof item === 'object' && item.url) return !item.url.startsWith('blob:');
      return true;
    });
  };

  // Helper function to check if a field path is dirty
  // CRITICAL: Used ONLY for file arrays to prevent accidental deletions
  const isFieldDirty = (dirtyFields, fieldPath) => {
    if (!dirtyFields || typeof dirtyFields !== 'object') return false;
    const parts = fieldPath.split('.');
    let current = dirtyFields;
    for (const part of parts) {
      if (!current || typeof current !== 'object' || !(part in current)) {
        return false;
      }
      current = current[part];
    }
    return current === true || (typeof current === 'object' && current !== null);
  };

  // Helper functions for working with nested paths
  const getByPath = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
  };

  const setByPath = (obj, path, value) => {
    const parts = path.split('.');
    let t = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!t[parts[i]]) {
        t[parts[i]] = {};
      }
      t = t[parts[i]];
    }
    t[parts[parts.length - 1]] = value;
  };

  const deleteByPath = (obj, path) => {
    const parts = path.split('.');
    let t = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!t || !t[parts[i]]) return;
      t = t[parts[i]];
    }
    delete t[parts[parts.length - 1]];
    // Clean up empty parent objects
    if (parts.length > 1) {
      let parent = obj;
      for (let i = 0; i < parts.length - 2; i++) {
        parent = parent[parts[i]];
      }
      if (parent && Object.keys(parent[parts[parts.length - 2]]).length === 0) {
        delete parent[parts[parts.length - 2]];
      }
    }
  };

  // Helper function to prepare update data with file handling
  // CRITICAL: Only send dirty fields to prevent unnecessary updates
  // For file arrays, we use dirty field checks to prevent accidental deletions
  const prepareUpdateData = (loadData, fileObjects, formData, dirtyFields) => {
    // First, get only dirty fields (except file arrays which we handle separately)
    let updateData = getDirtyFieldsData(loadData, dirtyFields) || {};
    
    // Clean blob URLs and File objects
    if (updateData && Object.keys(updateData).length > 0) {
      updateData = cleanFileData(updateData);
      if (updateData && typeof updateData === 'object') {
        Object.keys(updateData).forEach(key => {
          if (updateData[key] === undefined) {
            delete updateData[key];
          }
        });
      }
    }

    // CRITICAL: File array fields - only include if dirty
    // This prevents accidental deletions when user didn't touch image fields
    const fileArrayFieldPaths = [
      'pickup.images',
      'delivery.images',
      'carrierPhotos',
      'vehicle.vehicleImages',
      'freight.freightImages',
      'bolDocuments',
      'rateConfirmationDocuments',
      'documents',
      'images',
      'pdfs',
      'vehicle.pdfs',
      'freight.pdfs',
    ];

    fileArrayFieldPaths.forEach((fieldPath) => {
      const isDirty = isFieldDirty(dirtyFields || {}, fieldPath);
      const formValue = getByPath(formData, fieldPath);
      const keys = Array.isArray(formValue) ? extractImageKeys(formValue) : [];

      if (!isDirty) {
        // Field not dirty - remove from patch (don't touch on server)
        deleteByPath(updateData, fieldPath);
        return;
      }

      // Field is dirty - include keys (even if empty array, which means CLEAR)
      setByPath(updateData, fieldPath, keys);
    });

    // Handle file uploads: merge existing keys with new files
    const fileFieldNames = new Set(fileObjects.map(({ fieldName }) => fieldName));
    
    if (fileObjects.length > 0) {
      const fieldMapping = {
        'pickupImages': { path: 'pickup.images', formPath: 'pickup.images' },
        'deliveryImages': { path: 'delivery.images', formPath: 'delivery.images' },
        'carrierPhotos': { path: 'carrierPhotos', formPath: 'carrierPhotos' },
        'vehicleImages': { path: 'vehicle.vehicleImages', formPath: 'vehicle.vehicleImages', remove: 'vehicle.images' },
        'freightImages': { path: 'freight.freightImages', formPath: 'freight.freightImages', remove: 'freight.images' },
        'bolDocuments': { path: 'bolDocuments', formPath: 'bolDocuments' },
        'rateConfirmationDocuments': { path: 'rateConfirmationDocuments', formPath: 'rateConfirmationDocuments' },
        'documents': { path: 'documents', formPath: 'documents' },
        'vehiclePdfs': { path: 'vehicle.pdfs', formPath: 'vehicle.pdfs' },
        'freightPdfs': { path: 'freight.pdfs', formPath: 'freight.pdfs' },
        'pdfs': { path: 'pdfs', formPath: 'pdfs' }
      };

      fileObjects.forEach(({ fieldName }) => {
        const mapping = fieldMapping[fieldName];
        if (!mapping) return;

        // Get existing keys from form (already normalized)
        const existingKeys = getByPath(formData, mapping.formPath);
        const normalizedKeys = Array.isArray(existingKeys) ? extractImageKeys(existingKeys) : [];

        // CRITICAL: Only set field if it's dirty OR if we have existing keys to preserve
        const fieldIsDirty = isFieldDirty(dirtyFields || {}, mapping.formPath);
        
        if (fieldIsDirty || normalizedKeys.length > 0) {
          // Set field with existing keys (new files will be merged on server)
          setByPath(updateData, mapping.path, normalizedKeys);
        }

        // Remove legacy fields if needed
        if (mapping.remove) {
          deleteByPath(updateData, mapping.remove);
        }
      });
    }

    // CRITICAL: Include deleted images arrays in patch
    // These arrays contain keys of images that user explicitly wants to delete
    const deletedImageFields = [
      { formField: 'deletedCarrierImages', patchField: 'deletedCarrierImages' },
      { formField: 'deletedPickupImages', patchField: 'deletedPickupImages' },
      { formField: 'deletedDeliveryImages', patchField: 'deletedDeliveryImages' },
      { formField: 'deletedVehicleImages', patchField: 'deletedVehicleImages' },
      { formField: 'deletedFreightImages', patchField: 'deletedFreightImages' },
      { formField: 'deletedBolDocuments', patchField: 'deletedBolDocuments' },
      { formField: 'deletedRateConfirmationDocuments', patchField: 'deletedRateConfirmationDocuments' },
      { formField: 'deletedDocuments', patchField: 'deletedDocuments' },
    ];

    deletedImageFields.forEach(({ formField, patchField }) => {
      const deletedKeys = formData[formField];
      if (Array.isArray(deletedKeys) && deletedKeys.length > 0) {
        // Normalize deleted keys (extract keys from URLs if needed)
        const normalizedDeletedKeys = deletedKeys.map(key => {
          if (typeof key === 'string') {
            return extractImageKeys([key])[0] || key;
          }
          return key;
        }).filter(Boolean);
        
        if (normalizedDeletedKeys.length > 0) {
          updateData[patchField] = normalizedDeletedKeys;
        }
      }
    });

    // CRITICAL: Include loadCarrierPeople and loadCustomerRepresentativePeoples
    // These are independent copies for this specific load
    // Always include them if they exist in formData (regardless of dirty status)
    const carrierPeople = getByPath(formData, 'carrier.people') || [];
    if (Array.isArray(carrierPeople)) {
      const safeArray = (arr) => Array.isArray(arr) ? arr : [];
      const safeString = (value) => String(value || '');
      const preserveType = (t) => (t != null && String(t).trim() !== '') ? String(t).trim() : 'driver';
      const normalized = safeArray(carrierPeople).map(person => ({
        type: preserveType(person.type),
        fullName: safeString(person.fullName || ''),
        email: safeString(person.email || ''),
        phoneNumber: safeString(person.phoneNumber || '')
      })).filter(person => person.fullName && person.fullName.trim() !== '');
      // Always include loadCarrierPeople (even if empty array - means clear all)
      updateData.loadCarrierPeople = normalized;
    }

    const customerPeoples = getByPath(formData, 'customer.representativePeoples') || [];
    if (Array.isArray(customerPeoples)) {
      const safeArray = (arr) => Array.isArray(arr) ? arr : [];
      const safeString = (value) => String(value || '');
      const mapped = safeArray(customerPeoples).map(person => ({
        fullName: safeString(person.fullName),
        email: safeString(person.email || ''),
        phoneNumber: safeString(person.phoneNumber || '')
      }));
      // Filter empty items
      const filtered = mapped.filter(person => person.fullName && person.fullName.trim() !== '');
      // Always include loadCustomerRepresentativePeoples (even if empty array - means clear all)
      updateData.loadCustomerRepresentativePeoples = filtered || [];
    }

    return updateData;
  };

  const sendLoadRequest = async (loadId, data, fileObjects) => {
    const hasFiles = fileObjects.length > 0;

    if (hasFiles) {
      const formData = new FormData();
      formData.append('load', JSON.stringify(data));
      fileObjects.forEach(({ fieldName, file }) => {
        formData.append(fieldName, file);
      });

      if (loadId) {
        return await loadService.updateLoad(loadId, formData);
      }
      return await loadService.createLoad(formData);
    }

    const requestData = loadId ? { load: data } : data;
    if (loadId) {
      return await loadService.updateLoad(loadId, requestData);
    }
    return await loadService.createLoad(requestData);
  };

  const onSubmit = async (data, submitFromStep = null) => {
    const submitStep = submitFromStep || currentStep;
    if (!canSubmit || !isStepEditable(submitStep)) {
      return;
    }
    setIsSubmitting(true);
    setSubmitError("");

    try {
      // Validation for create mode only
      // TEMPORARILY: For updates, we now send all data (dirty fields logic disabled)
      if (!isEditMode) {
        const shouldValidateCarrier = isStep5Visited || submitStep === 5;
        const validation = validateLoadData(data, false, !shouldValidateCarrier);
        if (!validation.isValid) {
          setIsSubmitting(false);
          if (validation.errors.length > 0 && validation.errors[0].field) {
            methods.setError(validation.errors[0].field, {
              type: "manual",
              message: validation.errors[0].message,
            });
          }
          const firstMessage =
            validation.errors.length > 0 ? validation.errors[0].message : "Validation failed";
          setResponseModal({
            isOpen: true,
            status: "error",
            title: "Validation error",
            message: firstMessage,
          });
          return;
        }
      }

      let loadData = transformLoadDataForAPI(data);
      
      // Add createdBy if available
      try {
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
          const payload = JSON.parse(atob(authToken.split('.')[1]));
          if (payload.id || payload.userId) {
            loadData.createdBy = payload.id || payload.userId;
          }
        }
      } catch (e) {
        // Continue without createdBy - server will handle authentication
      }

      const shouldIncludeCarrier = isStep5Visited || submitFromStep !== 4;

      if (shouldIncludeCarrier && loadData.carrier && (
        loadData.carrier.name || 
        loadData.carrier.phoneNumber || 
        loadData.carrier.email || 
        loadData.carrier.companyName
      )) {
        try {
          await processCarrier(loadData);
        } catch (carrierError) {
          setResponseModal({
            isOpen: true,
            status: "error",
            title: "Carrier Creation Failed",
            message: carrierError.message,
          });
          setIsSubmitting(false);
          return;
        }
      } else if (!shouldIncludeCarrier) {
        delete loadData.carrier;
      }

      const fileObjects = hasFileObjects(data) ? extractAllFileObjects(data) : [];

      if (isEditMode && loadId) {
        // Validate that we have a valid loadId
        if (!loadId || typeof loadId !== 'string') {
          setIsSubmitting(false);
          setSubmitError("Invalid load ID");
          console.error('[LoadForm] Invalid loadId:', loadId);
          return;
        }

        // CRITICAL: For file arrays, we MUST use dirty field checks to prevent accidental deletions
        // Get dirtyFields for file array safety checks
        const dirtyFields = formState?.dirtyFields || {};
        
        // Prepare update data with file handling
        // Only send dirty fields (prevents sending entire object when nothing changed)
        // File arrays: only included if dirty (prevents accidental deletions)
        const patch = prepareUpdateData(loadData, fileObjects, data, dirtyFields);
        
        // Check if there are any changes to send
        const hasChanges = Object.keys(patch).length > 0 || fileObjects.length > 0;
        
        if (!hasChanges) {
          // No changes detected - don't send request
          setIsSubmitting(false);
          setResponseModal({
            isOpen: true,
            status: "success",
            title: "No Changes",
            message: "No changes were made to the load.",
          });
          return;
        }
        
       

        const response = await sendLoadRequest(loadId, patch, fileObjects);
        const updatedLoad = response?.data?.data;

        if (updatedLoad) {
          const docKeys = (arr) => (Array.isArray(arr) ? arr : []);
          methods.setValue('bolDocuments', docKeys(updatedLoad.bolDocuments), { shouldDirty: false });
          methods.setValue('rateConfirmationDocuments', docKeys(updatedLoad.rateConfirmationDocuments), { shouldDirty: false });
          methods.setValue('documents', docKeys(updatedLoad.documents), { shouldDirty: false });
          methods.setValue('deletedBolDocuments', [], { shouldDirty: false });
          methods.setValue('deletedRateConfirmationDocuments', [], { shouldDirty: false });
          methods.setValue('deletedDocuments', [], { shouldDirty: false });
        }

        // Reset carrier-related fields if status changed to "Listed"
        if (loadData.status === 'Listed') {
          methods.setValue('carrier', {}, { shouldDirty: false });
          methods.setValue('carrierEmails', [], { shouldDirty: false });
          methods.setValue('carrierPhotos', [], { shouldDirty: false });
          methods.setValue('carrierRate', '', { shouldDirty: false });
          methods.setValue('tracking', '', { shouldDirty: false });
          methods.setValue('fees', [], { shouldDirty: false });
          methods.setValue('tonu', { enabled: false, carrierRate: '', customerRate: '' }, { shouldDirty: false });
          methods.setValue('paymentMethod', '', { shouldDirty: false });
          methods.setValue('paymentMethodOther', '', { shouldDirty: false });
          methods.setValue('paymentTerms', '', { shouldDirty: false });
          methods.setValue('insurance', {}, { shouldDirty: false });
          methods.setValue('bolDocuments', [], { shouldDirty: false });
          methods.setValue('rateConfirmationDocuments', [], { shouldDirty: false });
          methods.setValue('documents', [], { shouldDirty: false });
          methods.setValue('tempEntityId', '', { shouldDirty: false });
          methods.setValue('dates', {
            assignedDate: '',
            deadline: '',
            pickupDate: '',
            pickupDateStart: '',
            pickupDateEnd: '',
            pickupDateType: 'Exact',
            deliveryDate: '',
            deliveryDateStart: '',
            deliveryDateEnd: '',
            deliveryDateType: 'Exact',
            aging: ''
          }, { shouldDirty: false });
          methods.setValue('pickup.images', [], { shouldDirty: false });
          methods.setValue('delivery.images', [], { shouldDirty: false });
        }

        setResponseModal({
          isOpen: true,
          status: "success",
          title: "Load Updated!",
          message: "Load information has been updated successfully.",
        });

        if (onSuccess) {
          onSuccess({ id: loadId, ...patch });
        }
      } else {
        const cleanedLoadData = removeEmptyFields(loadData);
        const response = await sendLoadRequest(null, cleanedLoadData, fileObjects);
        const createdLoad = response?.data?.data;

        if (createdLoad) {
          const docKeys = (arr) => (Array.isArray(arr) ? arr : []);
          methods.setValue('bolDocuments', docKeys(createdLoad.bolDocuments), { shouldDirty: false });
          methods.setValue('rateConfirmationDocuments', docKeys(createdLoad.rateConfirmationDocuments), { shouldDirty: false });
          methods.setValue('documents', docKeys(createdLoad.documents), { shouldDirty: false });
          methods.setValue('deletedBolDocuments', [], { shouldDirty: false });
          methods.setValue('deletedRateConfirmationDocuments', [], { shouldDirty: false });
          methods.setValue('deletedDocuments', [], { shouldDirty: false });
        }

        const successMessage = submitFromStep === 4
          ? "Load has been created successfully. Carrier information can be added later."
          : "Load has been created successfully.";

        setResponseModal({
          isOpen: true,
          status: "success",
          title: "Load Created!",
          message: successMessage,
        });

        if (onSuccess) {
          onSuccess(cleanedLoadData);
        }
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to process load. Please try again.";

      // Show error modal
      setResponseModal({
        isOpen: true,
        status: "error",
        title: "Operation Failed",
        message: errorMessage,
      });

      if (error?.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        Object.keys(apiErrors).forEach((field) => {
          methods.setError(field, {
            type: "manual",
            message: apiErrors[field],
          });
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const CurrentStepComponent = steps[currentStep - 1]?.component;

  if (loading) {
    return (
      <div className="load-add-form">
        <Loading size="large" />
      </div>
    );
  }

  return (
    <div className="load-add-form">
      <div className="form-header">
        <h2 className="page-title">
          <span className="page-name">Loads</span> <span>/</span>{" "}
          <span>{isEditMode ? "Edit Load" : "Add New Loads"}</span>
        </h2>
      </div>

      <div className="load-form-header">
        <StepSelector currentStep={currentStep} onStepClick={handleStepClick} />
      </div>

      <div className="form-content">
        <FormProvider {...methods}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <TestDataSelector isEditMode={isEditMode} />
          </div>
          <form
            onSubmit={handleSubmit(
              (data) => {
                if (currentStep === 4 || currentStep === 5) {
                  onSubmit(data, currentStep);
                }
              },
              handleFormInvalid
            )}
          >
            {/* Submit Error */}
            {submitError && (
              <div
                className="form-error"
                style={{
                  padding: "12px",
                  marginBottom: "16px",
                  backgroundColor: "#fee",
                  border: "1px solid #fcc",
                  borderRadius: "4px",
                  color: "#c33",
                }}
              >
                {submitError}
              </div>
            )}

            {CurrentStepComponent && (
              <CurrentStepComponent 
                onSubmitFromStep4={currentStep === 4 && canSubmitCurrentStep ? async (step) => {
                  const data = methods.getValues();
                  await onSubmit(data, step);
                } : undefined}
                isSubmitting={isSubmitting}
                readOnly={currentStepReadOnly}
                loadId={loadId}
              />
            )}

            <div className="form-navigation">
              <button
                type="button"
                className="back-btn"
                onClick={handleBack}
                disabled={currentStep === 1 || isSubmitting}
              >
                Back
              </button>

              <div className="form-navigation-right">
                {currentStep < steps.length ? (
                  <button
                    type="button"
                    className="next-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNext(e);
                    }}
                    disabled={isSubmitting}
                  >
                    Next
                  </button>
                ) : null}
                
                {currentStep === 4 || currentStep === 5 ? (
                  <button
                    type="button"
                    className="submit-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const submitStep = currentStep === 4 ? 4 : 5;
                      handleSubmit(
                        (data) => onSubmit(data, submitStep),
                        handleFormInvalid
                      )();
                    }}
                    disabled={isSubmitting || !canSubmitCurrentStep}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                ) : null}
              </div>
            </div>
          </form>
        </FormProvider>
      </div>

      {/* Response Modal */}
      <ResponseModal
        isOpen={responseModal.isOpen}
        status={responseModal.status}
        title={responseModal.title}
        message={responseModal.message}
        onClose={handleResponseModalClose}
      />
    </div>
  );
};

export default LoadAddForm;
